import axios from 'axios';
import request from 'store/utils/request';
import ErrorHandler from 'store/utils/ErrorHandler';
import readFile from 'store/utils/readFile';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';
import downloadFile from 'store/utils/downloadFile';
import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

/**
 * Get projects
 *
 * @param params
 */
export const getXpacks = (params = {}) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_XPACKS_REQUEST,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      ...params,
    };

    await dispatch({
      type: types.UPDATE_XPACKS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const { data } = await api.getXpacksRequest(finalParams);

    await dispatch({
      type: types.GET_XPACKS_SUCCESS,
      payload: {
        xpacks: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_XPACKS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add an xpack
 *
 * @param data
 */
export const addXpack = ({ file }) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_XPACK_REQUEST,
    });

    await api.addXpackRequest(file);

    await dispatch({
      type: types.ADD_XPACK_SUCCESS,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      page: 1,
    };

    await dispatch({
      type: types.UPDATE_XPACKS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getXpacksRequest(finalParams);

    await dispatch({
      type: types.GET_XPACKS_SUCCESS,
      payload: {
        xpacks: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({
      title: 'xPack created successfully!',
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_XPACK_ERROR,
      payload: {
        error: error.message,
      },
    });

    if (axios.isCancel(error)) {
      Notify.success({
        title: 'Adding Xpack was cancelled!',
      });
    } else if (error.response) {
      Notify.error({
        title: 'An error occurred',
        description: ErrorHandler.getNotificationError(error),
      });
    } else {
      Notify.error({
        title: 'An error occurred',
        description: 'Something went wrong while importing this xpack',
      });
    }

    throw error;
  }
};

export const cancelAddXpack = () => dispatch => {
  request.cancel('add-xpack');

  dispatch({
    type: types.CANCEL_ADD_XPACK,
  });
};

/**
 * Update an xPack
 *
 * @param xPack
 * @param data
 */
export const updateXpack = (xPack, data) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_XPACK_REQUEST,
    });

    await api.updateXpackRequest(xPack.id, data);

    await dispatch({
      type: types.UPDATE_XPACK_SUCCESS,
    });

    const finalParams = selectors.getPaginationOptions(getState());

    const response = await api.getXpacksRequest(finalParams);

    await dispatch({
      type: types.GET_XPACKS_SUCCESS,
      payload: {
        xpacks: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({ title: 'xPack updated successfully!' });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_XPACK_ERROR,
      payload: {
        error: error.message,
      },
    });

    if (axios.isCancel(error)) {
      Notify.success({
        title: 'Updating xPack was cancelled!',
      });
    } else if (error.response) {
      Notify.error({
        title: 'An error occurred',
        description: ErrorHandler.getNotificationError(error),
      });
    } else {
      Notify.error({
        title: 'An error occurred',
        description: 'Something went wrong while importing this xPack',
      });
    }

    throw error;
  }
};

export const cancelUpdateXpack = () => dispatch => {
  request.cancel('update-xpack');

  dispatch({
    type: types.CANCEL_UPDATE_XPACK,
  });
};

/**
 * Delete a project
 *
 * @param xPack
 */
export const deleteXpack = xpack => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_XPACK_REQUEST,
      payload: {
        xpack,
      },
    });

    await api.deleteXpackRequest(xpack.id);

    await dispatch({
      type: types.DELETE_XPACK_SUCCESS,
    });

    const paginationOptions = selectors.getPaginationOptions(getState());

    const finalParams = {
      ...paginationOptions,
      page: getCurrentPage(
        paginationOptions.page,
        selectors.getXpacks(getState()),
      ),
    };

    await dispatch({
      type: types.UPDATE_XPACKS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const { data } = await api.getXpacksRequest(finalParams);

    await dispatch({
      type: types.GET_XPACKS_SUCCESS,
      payload: {
        xpacks: data.results,
        count: data.count,
      },
    });

    Notify.success({
      title: 'xPack was deleted successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.DELETE_XPACK_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Download an xPack
 */
export const downloadXpack = xPack => async dispatch => {
  try {
    await dispatch({
      type: types.DOWNLOAD_XPACK_REQUEST,
    });

    const { data } = await api.downloadXpackRequest(xPack.id);

    await dispatch({
      type: types.DOWNLOAD_XPACK_SUCCESS,
    });

    await downloadFile(data, `${xPack.name}.nlu`);
  } catch (error) {
    await dispatch({
      type: types.DOWNLOAD_XPACK_ERROR,
      payload: {
        error: error.message,
      },
    });

    if (error.response) {
      const { data, status } = error.response;

      if (status === 403) {
        Notify.error({
          title: 'An error occurred',
          description: "You don't have permission to download this xPack",
        });
      } else if (data instanceof Blob) {
        try {
          const file = await readFile(data);
          const { detail } = JSON.parse(file);

          Notify.error({
            title: 'An error occurred',
            description: detail,
          });
        } catch (readError) {
          Notify.error({
            title: 'An error occurred',
            description: 'Something went wrong while downloading this xPack',
          });
        }
      } else {
        Notify.error({
          title: 'An error occurred',
          description: ErrorHandler.getNotificationError(error),
        });
      }
    } else {
      Notify.error({
        title: 'An error occurred',
        description: 'Something went wrong while downloading this xPack',
      });
    }
  }
};

export const overrideAllXpackIntents = (xpackId, value) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.OVERRIDE_ALL_XPACK_INTENTS_REQUEST,
    });

    await api.overrideAllXpackIntentsRequest(xpackId);

    dispatch({
      type: types.OVERRIDE_ALL_XPACK_INTENTS_COMPLETE,
      payload: {
        error: '',
      },
    });

    const finalParams = selectors.getPaginationOptions(getState());

    dispatch(getXpacks(finalParams));

    const notificationTitle = `Constraint Sets deactivation was ${
      value ? 'set' : 'unset'
    } successfully`;

    Notify.success({
      title: notificationTitle,
    });
  } catch (error) {
    dispatch({
      type: types.OVERRIDE_ALL_XPACK_INTENTS_COMPLETE,
      payload: {
        error,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const disableAllXpackIntents = (xpackId, value) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.DISABLE_ALL_XPACK_INTENTS_REQUEST,
    });

    await api.disableAllXpackIntentsRequest(xpackId);

    dispatch({
      type: types.DISABLE_ALL_XPACK_INTENTS_COMPLETE,
      payload: {
        error: '',
      },
    });

    const finalParams = selectors.getPaginationOptions(getState());

    dispatch(getXpacks(finalParams));

    const notificationTitle = `Intents deactivation was  ${
      value ? 'set' : 'unset'
    } successfully`;

    Notify.success({
      title: notificationTitle,
    });
  } catch (error) {
    dispatch({
      type: types.DISABLE_ALL_XPACK_INTENTS_COMPLETE,
      payload: {
        error,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};
